import { useMemo } from "react";
import { buildWorkspaceDetailDraggable } from "./handlers/workspaceDetailDrop";
import type { DraggableData } from "./ids";
import type { WorkspaceDetailDropContext } from "./TransferDndProvider";
import { useWorkspaceDetailDropContext } from "./TransferDndProvider";

/** Legacy mixed-selection payload when canvas + file are selected together. */
export type WorkspaceDetailSelectionSnapshot = {
  selectionSnapshot: string[];
};

export type WorkspaceDetailDragPayload =
  | DraggableData
  | WorkspaceDetailSelectionSnapshot;

/**
 * Build draggable data for workspace detail/index rows and cards.
 * Homogeneous selections become unified `DraggableData`; mixed
 * canvas+file selections fall back to `selectionSnapshot` for the
 * dispatcher's mixed-drop path.
 */
export function buildWorkspaceDetailDragPayload(
  selectionIds: string[],
  ctx: WorkspaceDetailDropContext | null,
): WorkspaceDetailDragPayload {
  if (!ctx) {
    return { selectionSnapshot: selectionIds };
  }

  const draggable = buildWorkspaceDetailDraggable(
    selectionIds,
    ctx.sourceWorkspaceId,
    ctx.canvasMap,
    ctx.fileRecordMap,
    ctx.inboxItemMap,
  );

  return draggable ?? { selectionSnapshot: selectionIds };
}

export function useWorkspaceDetailDragPayload(
  selectionIds: string[],
): WorkspaceDetailDragPayload {
  const ctx = useWorkspaceDetailDropContext();
  const selectionKey = selectionIds.join("\0");

  return useMemo(
    () => buildWorkspaceDetailDragPayload(selectionIds, ctx),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectionKey encodes selectionIds
    [ctx, selectionKey],
  );
}
